import { useEffect, useRef, useImperativeHandle, forwardRef } from 'react'
import { debounce } from 'lodash'
import Box from '@mui/material/Box'
import { Terminal as XTerm } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import '@xterm/xterm/css/xterm.css'

const Terminal = forwardRef(function Terminal({ onData, onResize, onReady }, ref) {
  const containerRef = useRef(null)
  const terminalRef = useRef(null)
  const fitAddonRef = useRef(null)

  useImperativeHandle(ref, () => ({
    write: (data) => {
      if (terminalRef.current) {
        terminalRef.current.write(data)
      }
    },
    clear: () => {
      if (terminalRef.current) {
        terminalRef.current.clear()
      }
    },
    focus: () => {
      if (terminalRef.current) {
        terminalRef.current.focus()
      }
    },
    fit: () => {
      if (fitAddonRef.current) {
        fitAddonRef.current.fit()
      }
    },
    getTerminal: () => terminalRef.current,
  }), [])

  useEffect(() => {
    if (!containerRef.current) return

    const terminal = new XTerm({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#1e1e1e',
        foreground: '#d4d4d4',
        cursor: '#d4d4d4',
        cursorAccent: '#1e1e1e',
        selection: 'rgba(255, 255, 255, 0.3)',
        black: '#000000',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#e5e510',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#f5f543',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#ffffff',
      },
    })

    const fitAddon = new FitAddon()
    const webLinksAddon = new WebLinksAddon()

    terminal.loadAddon(fitAddon)
    terminal.loadAddon(webLinksAddon)
    terminal.open(containerRef.current)

    // Initial fit
    const initialFitTimer = setTimeout(() => {
      fitAddon.fit()
    }, 0)

    terminalRef.current = terminal
    fitAddonRef.current = fitAddon

    // Handle input
    const dataDisposable = terminal.onData((data) => {
      if (onData) {
        onData(data)
      }
    })

    // Handle resize
    const resizeDisposable = terminal.onResize(({ cols, rows }) => {
      if (onResize) {
        onResize(cols, rows)
      }
    })

    // Debounced fit function that only resizes when dimensions actually change
    const debouncedFit = debounce(() => {
      const dimensions = fitAddon.proposeDimensions()
      if (dimensions &&
          (dimensions.cols !== terminal.cols || dimensions.rows !== terminal.rows)) {
        fitAddon.fit()
      }
    }, 100)

    // ResizeObserver for container size changes
    const resizeObserver = new ResizeObserver(debouncedFit)
    resizeObserver.observe(containerRef.current)

    // Notify ready
    if (onReady) {
      onReady(terminal)
    }

    return () => {
      clearTimeout(initialFitTimer)
      debouncedFit.cancel()
      dataDisposable.dispose()
      resizeDisposable.dispose()
      resizeObserver.disconnect()
      terminal.dispose()
      terminalRef.current = null
      fitAddonRef.current = null
    }
  }, [onData, onResize, onReady])

  return (
    <Box
      ref={containerRef}
      sx={{
        height: '100%',
        width: '100%',
        bgcolor: '#1e1e1e',
        '& .xterm': {
          height: '100%',
          padding: 1,
        },
        '& .xterm-viewport': {
          overflowY: 'scroll',  // Always show scrollbar for consistent width calculation
        },
        '& .xterm-screen': {
          width: '100%',
        },
      }}
    />
  )
})

export default Terminal
